import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { ScrollTop } from '@/components/element/ScrollTop';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { useNavState } from '@/providers/NavStateProvider';
import { useEffect, useState } from 'react';
import { useCurrentSalesOrder } from '@/providers/SalesOrderProvider';
import { useBillingSchedules, useBillingSchedulesInvalidate } from '@/hooks/api/1350_billing_schedules';
import { BillingSchedule, BillingScheduleDetail } from '@/hooks/api/models';
import { Link, useNavigate } from 'react-router-dom';
import { useLoading } from '@/providers/LoadingProvider';
import { useCreateInvoiceTask } from '@/hooks/api/1810_create_invoice_task';
import { useInvoiceTasks, useInvoiceTasksInvalidate } from '@/hooks/api/1800_invoice_tasks';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { toastApiError, toastSuccess } from '@/lib/toast';
import { ApiErrorMessages } from '@/components/element/ApiErrorMessages';
import { FormGroup } from '@/components/element/Form/FormGroup';
import Flatpickr from 'react-flatpickr';
import { Japanese } from 'flatpickr/dist/l10n/ja';
import moment from 'moment';

export const InvoiceTasksNew = () => {
  const { _w_, _t_ } = useCurrentTeamMember();
  const { setTitle, setBreadcrumbs } = useNavState();
  useEffect(() => setTitle(`新規請求タスク作成`), []);
  useEffect(
    () =>
      setBreadcrumbs([
        { label: `プロジェクト（売上） - ${_t_!.name}`, path: `/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders` },
        { label: `プロジェクト（売上）詳細`, path: `/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders/${_so_!.id}` },
      ]),
    []
  );

  const { _so_ } = useCurrentSalesOrder();
  const { data: schedules, refetch } = useBillingSchedules({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _so_!.id,
  });

  if (schedules == null) return <></>;

  return (
    <WorkspaceLayout>
      <ScrollTop />

      {schedules && <_InvoiceTasksNew schedules={schedules} />}
    </WorkspaceLayout>
  );
};

type FormInputs = {
  identifier: string;
  team_id: number;
  sales_order_id: number;
  name: string;
  description?: string;
  billing_schedule_detail_id: number;
  due_date?: string;
};

const _InvoiceTasksNew = ({ schedules }: { schedules: BillingSchedule[] }) => {
  const navigate = useNavigate();
  const { setShowLoading } = useLoading();
  const { _w_, _t_, me } = useCurrentTeamMember();
  const { _so_ } = useCurrentSalesOrder();
  const [scheduleDetails, setScheduleDetails] = useState<
    {
      year_month: string;
      detail: BillingScheduleDetail;
    }[]
  >([]);
  const { mutate, data, isPending, error } = useCreateInvoiceTask();
  const [autoSettingName, setAutoSettingName] = useState('');
  const { invalidate } = useInvoiceTasksInvalidate(_w_!.identifier);
  const { invalidate: invalidateBillingSchedules } = useBillingSchedulesInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _so_!.id,
  });
  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      sales_order_id: _so_!.id,
    },
  });

  const [billingScheduleDetailId, name] = watch(['billing_schedule_detail_id', 'name']);

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    if (schedules) {
      const _schedules: { year_month: string; detail: BillingScheduleDetail }[] = [];
      schedules.forEach((d) => {
        if (d.spot_detail && d.spot_closed == false)
          _schedules.push({ year_month: d.year_month, detail: d.spot_detail });
        if (d.monthly_detail && d.monthly_closed == false)
          _schedules.push({ year_month: d.year_month, detail: d.monthly_detail });
      });
      setScheduleDetails(_schedules);

      if (_schedules.length > 0) {
        setValue('billing_schedule_detail_id', _schedules[0].detail.id);
      }
    }
  }, [schedules]);

  // タスク名を自動設定する
  useEffect(() => {
    if (billingScheduleDetailId) {
      const schedule = scheduleDetails.find((s) => s.detail.id == billingScheduleDetailId);

      if (autoSettingName == '' || name == autoSettingName) {
        const _prefix = schedule
          ? `${schedule.year_month} ${schedule.detail.sales_context.name == 'spot' ? '一括' : '月額'}`
          : '';

        const _n = `${_prefix} 請求 - ${_so_!.name}`;
        setValue('name', _n);
        setAutoSettingName(_n);
      }

      if (schedule) {
        const ymd = moment(new Date(`${schedule?.year_month}/01`).toISOString())
          .endOf('month')
          .format('YYYY/MM/DD');
        setValue('due_date', ymd);
      }
    }
  }, [billingScheduleDetailId]);

  useEffect(() => {
    if (data) {
      invalidate();
      invalidateBillingSchedules();
      toastSuccess('請求タスクの登録が完了しました');
      navigate(`/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders/${_so_!.id}/invoice_tasks/${data.id}`);
    }
  }, [data]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12 col-lg-9 mx-auto">
          <div className="card mb-4" id="basic-info">
            <div className="card-header d-flex align-items-center">
              <div className="col-md-8">
                <h5 className="mb-1">新規請求タスク作成</h5>
                <p className="text-xs mb-0">請求書を管理するタスクを作成します。</p>
              </div>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="card-body pt-0 row">
                <ApiErrorMessages error={error} />

                <div className="form-group">
                  <label>プロジェクト名</label>
                  <p className="form-text text-muted text-sm ms-1">{_so_!.name}</p>
                </div>
                <FormGroup label="タスク名" required={true}>
                  <input
                    type="text"
                    maxLength={50}
                    className={`form-control ${errors.name && 'is-invalid'}`}
                    placeholder={'タスク名'}
                    {...register('name', {
                      required: '入力して下さい。',
                    })}
                  />
                  {errors.name && <div className="text-xs text-danger mt-1">{errors.name.message}</div>}
                </FormGroup>

                <FormGroup label="メモ">
                  <textarea className="form-control" maxLength={2000} rows={5} {...register('description')}></textarea>
                </FormGroup>

                <div className="col-12 col-md-6">
                  <FormGroup label="請求月" required={true}>
                    <select className="form-control" required={true} {...register('billing_schedule_detail_id')}>
                      {scheduleDetails.map((d) => (
                        <option key={`bsd-${d.detail.id}`} value={d.detail.id}>
                          {d.year_month} {d.detail.sales_context.name == 'spot' ? '一括' : '月額'}
                        </option>
                      ))}
                    </select>
                  </FormGroup>
                </div>
                <div className="col-12 col-md-6">
                  <FormGroup label="期限日">
                    <Controller
                      control={control}
                      name="due_date"
                      render={({ field: { ref, ...field } }) => (
                        <Flatpickr
                          className={`form-control datepickr`}
                          {...field}
                          onChange={(_, dateString) => {
                            field.onChange(dateString);
                            field.onBlur();
                          }}
                          onBlur={() => {}}
                          options={{ locale: Japanese, dateFormat: 'Y/m/d' }}
                        />
                      )}
                    />
                  </FormGroup>
                </div>
              </div>
              <div className="card-footer d-flex align-items-center justify-content-end pt-0">
                <Link to="" className="btn btn-outline-secondary me-2" onClick={() => navigate(-1)}>
                  戻る
                </Link>
                <button className="btn bg-gradient-dark btn-icon">
                  <i className="fas fa-check-circle"></i>登録する
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
